import { type datadogLogs as DatadogLogger } from '@datadog/browser-logs';

import { type Severity, getSeverities } from './datadog.js';
import { type Logger } from './model.js';

/**
 * Map Datadog Browser log functions onto their console counterparts
 * so that both are called when the logger is used. Only send
 * to Datadog if the severity level is high enough.
 */
const buildDatadogBrowserLogFns = ({
  logger,
  ddLogger,
  maxSeverity = 'warn',
}: {
  logger: Logger;
  ddLogger: (typeof DatadogLogger)['logger'];
  maxSeverity: Severity | 'all';
}) => {
  getSeverities(maxSeverity).forEach((severity) => {
    const originalFn = logger[severity];
    logger[severity] = (...args) => {
      originalFn(...args);
      ddLogger[severity](...args);
    };
  });
};

/**
 * Initialize Datadog by proxying logger calls and storing the root Datadog
 * object on the logger itself as an escape hatch.
 * Use a different logger for the browser or server.
 */
export const initDatadog = async (logger: Logger) => {
  const { datadogLogs } = await import('@datadog/browser-logs');

  if (!process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN) {
    throw new Error(
      'Tried to initialize Datadog Browser Logs without a client token!',
    );
  }

  datadogLogs.init({
    clientToken: `${process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN}`,
    site: process.env.NEXT_PUBLIC_DATADOG_SITE,
    env: process.env.NEXT_PUBLIC_DATADOG_ENV,
    forwardReports: 'all',
    sessionSampleRate: 10,
    silentMultipleInit: true, // Prevent logging errors if Next.js loads this chunk twice
  });

  logger.datadogBrowser = datadogLogs;

  buildDatadogBrowserLogFns({
    logger,
    ddLogger: datadogLogs.logger,
    maxSeverity:
      (process.env.NEXT_PUBLIC_DATADOG_SEVERITY as Severity) || 'all',
  });
};
